import { OctagonX, TriangleAlert } from 'lucide-react'

import { useCatalog } from '@/catalog/CatalogProvider'
import { MethodBadge } from '@/components/MethodBadge'
import { isFlagged, splitEndpointKey, type EndpointStatus, type OpApiStatus } from '@/lib/apiCatalog'
import { fmtTimestamp } from '@/lib/http'
import { endpointKey } from '@/lib/useCoveredBy'

const NOTES: Partial<Record<OpApiStatus['status'], string>> = {
  not_checked: 'Not checked against the Atlassian spec yet',
  outside_catalog: 'Outside the Atlassian API catalog',
}

/** A `Sunset` header is an HTTP date; show it as a date when it parses. */
function sunsetDate(raw: string): string {
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? raw : fmtTimestamp(d.toISOString().slice(0, 10))
}

function facts(ep: EndpointStatus): string[] {
  const out: string[] = []
  if (ep.removed_at) out.push(`Removed from the spec on ${fmtTimestamp(ep.removed_at.slice(0, 10))}`)
  else if (ep.deprecated) out.push('Deprecated')
  if (ep.sunset) out.push(`Sunset ${sunsetDate(ep.sunset)}`)
  return out
}

/**
 * What Atlassian says about this operation's endpoints: a banner for deprecated / removed,
 * a quiet note when the operation can't be vouched for (not checked, outside the catalog).
 */
export function ApiStatusBanner({ status, className = 'mb-6' }: {
  status: OpApiStatus | undefined
  /** outer spacing; the default suits a page body */
  className?: string
}) {
  const catalog = useCatalog()
  if (!status) return null
  if (!isFlagged(status)) {
    const note = NOTES[status.status]
    return note ? <p className={`text-[12px] text-text-3 ${className}`}>{note}</p> : null
  }

  // endpoint key → the spec's real path ({issueIdOrKey} rather than {})
  const realPath = new Map<string, string>()
  for (const p of catalog.products)
    for (const c of p.categories)
      for (const op of c.operations) realPath.set(endpointKey(p.key, op.method, op.path), op.path)

  const removed = status.status === 'removed'
  const Icon = removed ? OctagonX : TriangleAlert
  const tone = removed ? 'text-danger' : 'text-warn'
  const endpoints = status.endpoints.filter((e) => e.removed_at || e.deprecated || e.sunset)
  // A removal note that already says "Use X instead" is not repeated below it.
  const replacement = status.replacement
  const showReplacement =
    !!replacement &&
    !endpoints.some((e) => e.removal_note?.toLowerCase().includes(replacement.toLowerCase()))

  return (
    <section
      aria-label="Atlassian API status"
      className={`max-w-2xl rounded-md border border-line-strong bg-surface px-4 py-3 ${className}`}
    >
      <div className="flex gap-2.5">
        <Icon aria-hidden className={`mt-px size-4 shrink-0 ${tone}`} />
        <div className="min-w-0 flex-1">
          <h2 className={`text-[13px] font-semibold ${tone}`}>
            {removed ? 'Removed by Atlassian' : 'Deprecated by Atlassian'}
          </h2>
          <ul className="mt-2 space-y-2.5">
            {endpoints.map((ep) => {
              const { method, path } = splitEndpointKey(ep.key)
              return (
                <li key={ep.key} className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    {method && <MethodBadge method={method} />}
                    <span className="truncate font-mono text-[12px] text-foreground" title={realPath.get(ep.key) ?? path}>
                      {realPath.get(ep.key) ?? path}
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] text-text-2">{facts(ep).join(' · ')}</p>
                  {ep.removal_note && (
                    <p className="mt-1 text-[12px] leading-relaxed text-text-3">{ep.removal_note}</p>
                  )}
                </li>
              )
            })}
          </ul>
          {showReplacement && (
            <p data-testid="api-replacement" className="mt-3 text-[12.5px] text-text-2">
              Use <span className="font-medium text-foreground">{replacement}</span> instead
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
